import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useSelector, useDispatch } from "react-redux";
import { SAGA_ACTIONS } from "src/_config";
import { useTranslation } from "src/_common/hooks/useTranslation";
import { useAppUserDetailsSelector } from "src/_common/hooks/selectors/userSelector";
import CurrencySelectionModal from "../../modal/CurrencySelectionModal";
import { useJobListSelector } from "src/_common/hooks/selectors/jobSelector.";
import { useParams } from "react-router-dom";
import { ReactSearchAutocomplete } from "react-search-autocomplete";

interface FormData {
  candidateName: string;
  jobTitle: string;
  department: string;
  companyName: string;
  startDate: string;
  salary: string;
  currencyType: string;
  employmentType: string;
  workplace: string;
  workingHours: string;
  reportingTo: string;
  benefits: string;
  includeDigitalSignature: boolean;
}

interface AIOfferLetterGeneratorProps {
  candidateData: {
    name: string;
    position: string;
  };
  // onGenerate: (data: FormData, currencyType: string) => void;
  onPreviewAndSend: (data: FormData, currencyType: string) => void;
  isGenerating: boolean;
}

export default function AIOfferLetterGenerator({
  candidateData,
  // onGenerate,
  onPreviewAndSend,
  isGenerating,
}: AIOfferLetterGeneratorProps) {
  const dispatch = useDispatch();
  const { t } = useTranslation("job-details");
  const userDetails = useAppUserDetailsSelector();

  // State for currency modal
  const [selectedCurrency, setSelectedCurrency] = useState<any | null>("USD");
  const [selectedCurrencySymbol, setSelectedCurrencySymbol] = useState<string>("$");
  const [showCurrencyModal, setShowCurrencyModal] = useState(false);

  // State for functional areas autocomplete
  const [filteredFunctionalAreas, setFilteredFunctionalAreas] = useState<any[]>([]);
  const [showManualDepartmentInput, setShowManualDepartmentInput] = useState(false);

  // Redux selector for job post types, currencies, and salary ranges
  const { jobPostTypes, currencies, salaryRanges, functionalAreas, workplaces } = useSelector((state: any) => ({
    jobPostTypes: state.global.jobPostTypes,
    currencies: state.global.currencies,
    salaryRanges: state.global.salaryRanges,
    functionalAreas: state.global.functionalAreas,
    workplaces: state.global.workplaces,
  }));

  // Use fallback data if no data from API
  const jobPostTypesData = jobPostTypes && jobPostTypes.length > 0 ? jobPostTypes : null;
  const currenciesData = currencies && currencies.length > 0 ? currencies : null;
  const salaryRangesData = salaryRanges && salaryRanges.length > 0 ? salaryRanges : null;
  const functionalAreasData = functionalAreas && functionalAreas.length > 0 ? functionalAreas : null;
  const workplacesData = workplaces && workplaces.length > 0 ? workplaces : null;

  const { jobId } = useParams<{ jobId: string }>();
  const jobListings = useJobListSelector();
  const currentJob = jobListings?.find((job) => job.emp_job_id == jobId);

  const { control, handleSubmit, watch, errors, reset } = useForm<FormData>({
    defaultValues: {
      candidateName: candidateData.name,
      jobTitle: candidateData.position,
      department: "",
      companyName: userDetails?.company?.name || "",
      startDate: "",
      salary: "",
      employmentType: currentJob?.job_type || "",
      workplace: currentJob?.workplace_type || "",
      workingHours: "40 hours per week",
      reportingTo: "",
      benefits: "",
      includeDigitalSignature: true,
    },
  });

  const formData = watch();

  // Update form values when candidate data changes
  useEffect(() => {
    reset({
      candidateName: candidateData.name,
      jobTitle: candidateData.position,
      department: "",
      companyName: userDetails?.company?.name || "",
      startDate: "",
      salary: "",
      employmentType: currentJob?.job_type || "",
      workplace: currentJob?.workplace_type || "",
      workingHours: "40 hours per week",
      reportingTo: "",
      benefits: "",
      includeDigitalSignature: true,
    });
  }, [
    candidateData.name,
    candidateData.position,
    userDetails?.company,
    currentJob?.job_type,
    currentJob?.workplace_type,
    reset,
  ]);

  // Set currency from currentJob
  useEffect(() => {
    if (currentJob?.currency_name) {
      setSelectedCurrency(currentJob.currency_name);
    }
  }, [currentJob]);

  // Initialize functional areas for autocomplete
  useEffect(() => {
    if (functionalAreasData) {
      setFilteredFunctionalAreas(functionalAreasData);
    }
  }, [functionalAreasData]);

  // Handle functional area search - call API as user types
  const handleFunctionalAreaSearch = (searchString: string) => {
    if (searchString && searchString.length > 1) {
      // Call API with search parameter when user types more than 1 character
      dispatch({
        type: SAGA_ACTIONS.COMMON.FUNCTIONAL_AREAS,
        payload: { limit: 100, offset: 0, name: searchString.trim() },
      });
    } else if (!searchString || searchString.length === 0) {
      // Load all functional areas when search is cleared
      dispatch({
        type: SAGA_ACTIONS.COMMON.FUNCTIONAL_AREAS,
        payload: { limit: 100, offset: 0 },
      });
    }
  };

  const handleGenerateClick = () => {
    // handleSubmit((data) => onGenerate(data, selectedCurrency))();
  };

  const handlePreviewAndSendClick = () => {
    handleSubmit((data) => onPreviewAndSend(data, selectedCurrency))();
  };

  // Fetch job post types, currencies, and salary ranges on component mount
  useEffect(() => {
    dispatch({
      type: SAGA_ACTIONS.COMMON.JOB_POST_TYPES,
      payload: { limit: 30, offset: 0 },
    });

    dispatch({
      type: SAGA_ACTIONS.COMMON.CURRENCIES,
      payload: { limit: 30, offset: 0 },
    });

    dispatch({
      type: SAGA_ACTIONS.COMMON.WORK_PLACES,
      payload: { limit: 30, offset: 0 },
    });

    // Load initial functional areas (smaller limit since we load on demand)
    // dispatch({
    //   type: SAGA_ACTIONS.COMMON.FUNCTIONAL_AREAS,
    //   payload: { limit: 100, offset: 0 },
    // });

    // dispatch({
    //   type: SAGA_ACTIONS.COMMON.SALARY_RANGES,
    //   payload: { limit: 30, offset: 0 },
    // });
  }, [dispatch]);

  // Close currency modal when pressing Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setShowCurrencyModal(false);
      }
    };

    if (showCurrencyModal) {
      document.addEventListener("keydown", handleEscape);
    }
    return () => document.removeEventListener("keydown", handleEscape);
  }, [showCurrencyModal]);

  return (
    <div className="ai-offer-generator">
      <div className="ai-offer-generator__header">
        <h3 className="ai-offer-generator__title">{t("jobDetails.finalDecision.aiGenerator.title")}</h3>
      </div>

      <p className="ai-offer-generator__description">{t("jobDetails.finalDecision.aiGenerator.description")}</p>

      <form>
        <div className="ai-offer-generator__form-row ai-offer-generator__form-row--two-cols">
          <div>
            <label className="ai-offer-generator__field-label">
              {t("jobDetails.finalDecision.aiGenerator.form.candidateName")}
            </label>
            <Controller
              name="candidateName"
              control={control}
              rules={{ required: t("jobDetails.finalDecision.aiGenerator.validation.candidateNameRequired") }}
              render={(props) => (
                <>
                  <input
                    {...props}
                    type="text"
                    className={`ai-offer-generator__input ${
                      errors.candidateName ? "ai-offer-generator__input--error" : ""
                    }`}
                  />
                  {errors.candidateName && (
                    <span className="ai-offer-generator__error-text">{errors.candidateName.message}</span>
                  )}
                </>
              )}
            />
          </div>

          <div>
            <label className="ai-offer-generator__field-label">
              {t("jobDetails.finalDecision.aiGenerator.form.jobTitle")}
            </label>
            <Controller
              name="jobTitle"
              control={control}
              rules={{ required: t("jobDetails.finalDecision.aiGenerator.validation.jobTitleRequired") }}
              render={(props) => (
                <>
                  <input
                    {...props}
                    type="text"
                    className={`ai-offer-generator__input ${errors.jobTitle ? "ai-offer-generator__input--error" : ""}`}
                  />
                  {errors.jobTitle && <span className="ai-offer-generator__error-text">{errors.jobTitle.message}</span>}
                </>
              )}
            />
          </div>
        </div>

        <div className="ai-offer-generator__form-row ai-offer-generator__form-row--two-cols">
          <div>
            <label className="ai-offer-generator__field-label">
              {t("jobDetails.finalDecision.aiGenerator.form.department")}
            </label>
            <Controller
              name="department"
              control={control}
              rules={{ required: t("jobDetails.finalDecision.aiGenerator.validation.departmentRequired") }}
              render={({ onChange, value }) => (
                <>
                  {!showManualDepartmentInput ? (
                    <>
                      <div className={`figma-autocomplete-wrapper ${errors.department ? "error" : ""}`}>
                        <ReactSearchAutocomplete
                          items={filteredFunctionalAreas}
                          onSearch={handleFunctionalAreaSearch}
                          onSelect={(item) => {
                            onChange(item.name);
                          }}
                          onClear={() => {
                            onChange("");
                          }}
                          autoFocus={false}
                          resultStringKeyName="name"
                          inputSearchString={value || ""}
                          showNoResults={false}
                          showNoResultsText=""
                          formatResult={(item: any) => (
                            <div className="autocomplete-result">
                              <span>{item.name}</span>
                            </div>
                          )}
                          placeholder={t("jobDetails.finalDecision.aiGenerator.form.selectDepartment")}
                          styling={{
                            border: "1px solid #D7D7D7",
                            borderRadius: "6px",
                            backgroundColor: "#F6F6F6",
                            boxShadow: "none",
                            color: "#374151",
                            lineColor: "#e5e7eb",
                            placeholderColor: "#999",
                            zIndex: 2,
                          }}
                          fuseOptions={{ keys: ["name"] }}
                        />
                      </div>
                      <div className="ai-offer-generator__add-new-wrapper">
                        <span className="ai-offer-generator__add-new-text">Not in the list?</span>
                        <button
                          type="button"
                          onClick={() => {
                            setShowManualDepartmentInput(true);
                            onChange(""); // Clear the autocomplete value
                          }}
                          className="ai-offer-generator__add-new-btn"
                        >
                          Add new
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <input
                        type="text"
                        value={value || ""}
                        onChange={(e) => onChange(e.target.value)}
                        className={`ai-offer-generator__input ${
                          errors.department ? "ai-offer-generator__input--error" : ""
                        }`}
                        placeholder="Enter department name"
                      />
                      <div className="ai-offer-generator__back-to-list-wrapper">
                        <button
                          type="button"
                          onClick={() => {
                            setShowManualDepartmentInput(false);
                            onChange(""); // Clear the manual input value
                          }}
                          className="ai-offer-generator__back-to-list-btn"
                        >
                          Back to list
                        </button>
                      </div>
                    </>
                  )}
                  {errors.department && (
                    <span className="ai-offer-generator__error-text">{errors.department.message}</span>
                  )}
                </>
              )}
            />
          </div>

          <div>
            <label className="ai-offer-generator__field-label">
              {t("jobDetails.finalDecision.aiGenerator.form.companyName")}
            </label>
            <Controller
              name="companyName"
              control={control}
              rules={{ required: t("jobDetails.finalDecision.aiGenerator.validation.companyNameRequired") }}
              render={(props) => (
                <>
                  <input
                    {...props}
                    type="text"
                    className={`ai-offer-generator__input ${
                      errors.companyName ? "ai-offer-generator__input--error" : ""
                    }`}
                  />
                  {errors.companyName && (
                    <span className="ai-offer-generator__error-text">{errors.companyName.message}</span>
                  )}
                </>
              )}
            />
          </div>
        </div>

        <div className="ai-offer-generator__form-row ai-offer-generator__form-row--two-cols">
          <div>
            <label className="ai-offer-generator__field-label">
              {t("jobDetails.finalDecision.aiGenerator.form.startDate")}
            </label>
            <Controller
              name="startDate"
              control={control}
              rules={{ required: t("jobDetails.finalDecision.aiGenerator.validation.startDateRequired") }}
              render={(props) => (
                <>
                  <input
                    {...props}
                    type="date"
                    min={new Date().toISOString().split("T")[0]}
                    className={`ai-offer-generator__input ${
                      errors.startDate ? "ai-offer-generator__input--error" : ""
                    }`}
                  />
                  {errors.startDate && (
                    <span className="ai-offer-generator__error-text">{errors.startDate.message}</span>
                  )}
                </>
              )}
            />
          </div>

          <div>
            <label className="ai-offer-generator__field-label">
              {t("jobDetails.finalDecision.aiGenerator.form.salary")}
            </label>
            <Controller
              name="salary"
              control={control}
              rules={{
                required: t("jobDetails.finalDecision.aiGenerator.validation.salaryRequired"),
                pattern: {
                  value: /^\d{3,9}$/,
                  message: t("jobDetails.finalDecision.aiGenerator.validation.salaryDigitsValidation"),
                },
              }}
              render={(props) => (
                <>
                  <div>
                    <input
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 9);
                        props.onChange(value);
                      }}
                      value={props.value}
                      type="text"
                      inputMode="numeric"
                      maxLength={9}
                      className={`ai-offer-generator__input ${errors.salary ? "ai-offer-generator__input--error" : ""}`}
                      placeholder={t("jobDetails.finalDecision.aiGenerator.form.enterSalary")}
                    />
                    <div className="currency-change">
                      <button
                        type="button"
                        className="currency-btn"
                        onClick={() => {
                          setShowCurrencyModal(true);
                        }}
                      >
                        {selectedCurrency} <span>{t("jobDetails.finalDecision.aiGenerator.form.changeCurrency")}</span>
                      </button>
                    </div>
                  </div>
                  {errors.salary && <span className="ai-offer-generator__error-text">{errors.salary.message}</span>}
                </>
              )}
            />
          </div>
        </div>

        <div className="ai-offer-generator__form-row ai-offer-generator__form-row--two-cols">
          <div>
            <label className="ai-offer-generator__field-label">
              {t("jobDetails.finalDecision.aiGenerator.form.employmentType")}
            </label>
            <Controller
              name="employmentType"
              control={control}
              rules={{ required: t("jobDetails.finalDecision.aiGenerator.validation.employmentTypeRequired") }}
              render={(props) => (
                <>
                  <select
                    {...props}
                    className={`ai-offer-generator__select ${
                      errors.employmentType ? "ai-offer-generator__select--error" : ""
                    }`}
                  >
                    <option value="">{t("jobDetails.finalDecision.aiGenerator.form.selectEmploymentType")}</option>
                    {jobPostTypesData ? (
                      jobPostTypesData.map((jobType: any, index: number) => (
                        <option key={index} value={jobType.name}>
                          {jobType.name}
                        </option>
                      ))
                    ) : (
                      <></>
                    )}
                  </select>
                  {errors.employmentType && (
                    <span className="ai-offer-generator__error-text">{errors.employmentType.message}</span>
                  )}
                </>
              )}
            />
          </div>

          <div>
            <label className="ai-offer-generator__field-label">
              {t("jobDetails.finalDecision.aiGenerator.form.workplace")}
            </label>
            <Controller
              name="workplace"
              control={control}
              rules={{ required: t("jobDetails.finalDecision.aiGenerator.validation.workplaceRequired") }}
              render={(props) => (
                <>
                  <select
                    {...props}
                    className={`ai-offer-generator__select ${
                      errors.workplace ? "ai-offer-generator__select--error" : ""
                    }`}
                  >
                    <option value="">{t("jobDetails.finalDecision.aiGenerator.form.selectWorkplace")}</option>
                    {workplacesData ? (
                      workplacesData.map((workplace: any, index: number) => (
                        <option key={index} value={workplace.name}>
                          {workplace.name}
                        </option>
                      ))
                    ) : (
                      <></>
                    )}
                  </select>
                  {errors.workplace && (
                    <span className="ai-offer-generator__error-text">{errors.workplace.message}</span>
                  )}
                </>
              )}
            />
          </div>
        </div>
        <div className="ai-offer-generator__form-row ai-offer-generator__form-row--two-cols align-items-end">
          <div>
            <label className="ai-offer-generator__field-label">
              {t("jobDetails.finalDecision.aiGenerator.form.workingHours")}
            </label>
            <Controller
              name="workingHours"
              control={control}
              rules={{ required: t("jobDetails.finalDecision.aiGenerator.validation.workingHoursRequired") }}
              render={(props) => (
                <>
                  <input
                    {...props}
                    type="text"
                    className={`ai-offer-generator__input ${
                      errors.workingHours ? "ai-offer-generator__input--error" : ""
                    }`}
                    placeholder=""
                  />
                  {errors.workingHours && (
                    <span className="ai-offer-generator__error-text">{errors.workingHours.message}</span>
                  )}
                </>
              )}
            />
          </div>

          <div>
            <label className="ai-offer-generator__field-label">
              {t("jobDetails.finalDecision.aiGenerator.form.reportingTo")}
            </label>
            <Controller
              name="reportingTo"
              control={control}
              rules={{ required: t("jobDetails.finalDecision.aiGenerator.validation.reportingToRequired") }}
              render={(props) => (
                <>
                  <input
                    {...props}
                    type="text"
                    className={`ai-offer-generator__input ${
                      errors.reportingTo ? "ai-offer-generator__input--error" : ""
                    }`}
                    placeholder=""
                  />
                  {errors.reportingTo && (
                    <span className="ai-offer-generator__error-text">{errors.reportingTo.message}</span>
                  )}
                </>
              )}
            />
          </div>
        </div>
        <div className="ai-offer-generator__form-row">
          <div>
            <label className="ai-offer-generator__field-label">
              {t("jobDetails.finalDecision.aiGenerator.form.benefits")}
            </label>
            <Controller
              name="benefits"
              control={control}
              rules={{ required: t("jobDetails.finalDecision.aiGenerator.validation.benefitsRequired") }}
              render={(props) => (
                <>
                  <textarea
                    {...props}
                    rows={4}
                    className={`ai-offer-generator__textarea ${
                      errors.benefits ? "ai-offer-generator__textarea--error" : ""
                    }`}
                    placeholder={t("jobDetails.finalDecision.aiGenerator.form.benefitsPlaceholder")}
                  />
                  {errors.benefits && <span className="ai-offer-generator__error-text">{errors.benefits.message}</span>}
                </>
              )}
            />
          </div>
        </div>

        <div className="ai-offer-generator__checkbox-wrapper">
          <Controller
            name="includeDigitalSignature"
            control={control}
            render={(props) => (
              <label className="ai-offer-generator__checkbox-label">
                <input
                  type="checkbox"
                  checked={props.value}
                  onChange={(e) => props.onChange(e.target.checked)}
                  className="ai-offer-generator__checkbox"
                />
                {t("jobDetails.finalDecision.aiGenerator.form.includeDigitalSignature")}
              </label>
            )}
          />
        </div>

        <div className="ai-offer-generator__actions">
          {/* <button type="button" onClick={handleGenerateClick} disabled={isGenerating} className="btn-cancel">
            {isGenerating
              ? t("jobDetails.finalDecision.aiGenerator.buttons.generating")
              : t("jobDetails.finalDecision.aiGenerator.buttons.generate")}
          </button> */}

          <button type="button" onClick={handlePreviewAndSendClick} disabled={isGenerating} className="btn-preview">
            {isGenerating
              ? t("jobDetails.finalDecision.aiGenerator.buttons.processing")
              : t("jobDetails.finalDecision.aiGenerator.buttons.previewAndSend")}
          </button>
        </div>
      </form>

      {/* Currency Selection Modal */}
      {showCurrencyModal && (
        <CurrencySelectionModal
          isOpen={showCurrencyModal}
          onClose={() => setShowCurrencyModal(false)}
          currenciesData={currenciesData}
          selectedCurrency={selectedCurrency}
          onCurrencySelect={(currency, currencySymbol) => {
            setSelectedCurrency(currency);
            setSelectedCurrencySymbol(currencySymbol);
          }}
        />
      )}
    </div>
  );
}
